import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHero, Container, Overline } from "../components/Section";
import { ArrowRight, Mail, Calendar, Award, BookOpen, Globe2, Users, Download } from "lucide-react";
import { API } from "../lib/api";

const PORTRAITS = [
  { src: "/images/pastor/barber-formal.jpg",      label: "Formal" },
  { src: "/images/pastor/barber-clergy.jpg",      label: "Clergy" },
  { src: "/images/pastor/barber-houndstooth.jpg", label: "Editorial" },
  { src: "/images/pastor/barber-casual.jpg",      label: "Cultural" },
  { src: "/images/pastor/barber-smzbt-shirt.jpg", label: "Casual" },
];

const PRESS_KIT_URL = `${API}/press-kit`;

const HIGHLIGHTS = [
  { icon: BookOpen, label: "Education", value: "B.A. Biology (High Honors), Hampton Institute · D.D., Eastern Theological Seminary" },
  { icon: Award, label: "Military", value: "Distinguished Military Graduate · U.S. Army Captain (1981–1986)" },
  { icon: Users, label: "Pastoring Since", value: "July 2000 — Sixth Mount Zion Baptist Temple" },
  { icon: Globe2, label: "Global Missions", value: "Kenya · Uganda · South Africa · Haiti · London · Brazil" },
];

const HONORS = [
  "Newport News Teacher of the Year (1993, 1996)",
  "1998 Outstanding Young Man in America",
  "2000 Role Model of the Year — 100 Black Men of America, Virginia Peninsula Chapter",
  "2000–2001 National Register's Who's Who in Executives & Professionals",
  "2001 Doctor of Divinity — Eastern Theological Seminary (Chairman of Board)",
  "2001 Hampton University Twenty Year Most Outstanding Alumnus",
  "Former Chaplain, National Hampton Alumni Association Inc.",
];

export default function Pastor() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = PORTRAITS[activeIdx];

  return (
    <div data-testid="pastor-page">
      <PageHero
        overline="Senior Pastor"
        title={<>Rev. <span className="brand-gradient-text">Jerome A. Barber</span></>}
        kicker="Senior Pastor of Sixth Mount Zion Baptist Temple & Executive Director of Six House, Inc. — a life lifted by faith, family, education, and global mission."
        image="/images/branding/pastor-worship.jpg"
      />

      <Container className="py-20">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Portrait + thumbnail rail */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <motion.img
                key={active.src}
                src={active.src}
                alt={`Rev. Jerome A. Barber — ${active.label}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full h-[680px] object-cover"
                data-testid="pastor-main-portrait"
              />
              <div className="absolute -bottom-6 -right-6 glass p-6 max-w-[280px]">
                <div className="text-xs uppercase tracking-widest text-gold mb-2 font-bold">Senior Pastor</div>
                <div className="font-sans text-2xl font-extrabold">Rev. Jerome A. Barber</div>
                <div className="text-sm text-white/65 mt-1">Sixth Mount Zion Baptist Temple<br/>President, Six House, Inc.</div>
              </div>
            </div>

            {/* Thumbnail rail */}
            <div className="mt-8 grid grid-cols-5 gap-2" data-testid="pastor-thumbnails">
              {PORTRAITS.map((p, i) => (
                <button
                  key={p.src}
                  onClick={() => setActiveIdx(i)}
                  className={`relative aspect-square overflow-hidden border transition-all ${
                    i === activeIdx ? "border-gold scale-[1.02]" : "border-white/10 hover:border-white/30 opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`pastor-thumb-${i}`}
                  aria-label={`View ${p.label} portrait`}
                >
                  <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Bio */}
          <div className="lg:col-span-7">
            <Overline>Pastor Bio</Overline>
            <h2 className="text-4xl md:text-5xl font-sans font-black leading-tight mb-8 tracking-[-0.03em]">A life of service, scholarship, & <span className="brand-gradient-text">sacred calling</span>.</h2>

            <p className="drop-cap text-lg leading-[1.85] text-white/90 font-sans">
              Rev. Jerome A. Barber is the Senior Pastor of Sixth Mount Zion Baptist Temple, a strong growing congregation in Hampton, Virginia, and the Executive Director of Six House, Inc., a faith-based community organization. A Washington, DC native, he completed his secondary education in the Maryland Public Schools, then earned his Bachelor of Arts in Biology from Hampton Institute in 1981 — graduating with high honors as a Distinguished Military Graduate and commissioned as 2nd Lieutenant in the US Army. He achieved the rank of Captain before leaving the Army in 1986.
            </p>

            <p className="mt-6 text-white/80 leading-relaxed text-lg">
              While serving Newport News Public Schools as a Science Resource Teacher, Rev. Barber was named <span className="text-gold font-semibold">Teacher of the Year in 1993 and again in 1996</span>. His passion for education and educators continues through "<em className="text-ember">Educarers</em>" — a bi-annual ministry he founded to celebrate the caring, compassionate heart of the teaching profession. His life-lifting messages are his trademark.
            </p>

            <p className="mt-6 text-white/80 leading-relaxed text-lg">
              Since July 2000, Pastor Barber has mobilized the Sixth Mount Zion congregation to take the gospel to the world — engaging each parishioner to discover their unique gift and employ it to meet the needs of the local community. He has led teams of pastors, medical professionals, educators, and youth on revivals, crusades, Bible seminars, conferences, workshops, and medical clinics across <span className="text-ember font-semibold">Kenya, Uganda, South Africa, Haiti, London, and Brazil</span>.
            </p>

            <p className="mt-6 text-white/80 leading-relaxed text-lg">
              The joy of Rev. Barber's life are the special blessings of his <span className="text-gold font-semibold">5 children and 9 grandchildren</span>.
            </p>

            <div className="flex flex-wrap gap-4 mt-12">
              <Link to="/book-pastor" className="btn-wine px-7 py-3.5 rounded-full text-sm uppercase tracking-wider font-bold inline-flex items-center gap-2" data-testid="pastor-booking-cta">
                <Calendar className="h-4 w-4" /> Book the Pastor
              </Link>
              <a
                href={PRESS_KIT_URL}
                download="Rev_Jerome_A_Barber_Press_Kit.pdf"
                className="btn-gold px-7 py-3.5 rounded-full text-sm uppercase tracking-wider font-bold inline-flex items-center gap-2"
                data-testid="pastor-press-kit-download"
              >
                <Download className="h-4 w-4" /> Download Press Kit
              </a>
              <a href="mailto:pastor@smzbt.org" className="btn-ghost px-7 py-3.5 rounded-full text-sm uppercase tracking-wider font-bold inline-flex items-center gap-2">
                <Mail className="h-4 w-4" /> Contact
              </a>
            </div>
            <p className="text-xs text-white/50 mt-4 max-w-md italic">
              Press Kit (PDF) includes long & short-form bios, approved photos, biography highlights, and media contact info — please use these official materials for media coverage and organizational use.
            </p>
          </div>
        </div>

        {/* Highlights grid */}
        <div className="mt-24">
          <Overline>At a Glance</Overline>
          <div className="grid sm:grid-cols-2 gap-px bg-white/5 mt-6">
            {HIGHLIGHTS.map((h) => (
              <div key={h.label} className="bg-royal/40 p-8 flex gap-5">
                <h.icon className="h-6 w-6 text-gold shrink-0 mt-1 stroke-[1.5]" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-ember mb-2 font-bold">{h.label}</div>
                  <div className="text-white/90 leading-relaxed">{h.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Honors */}
        <div className="mt-20">
          <Overline>Honors & Recognition</Overline>
          <h3 className="text-3xl md:text-4xl font-sans font-black mb-10 tracking-[-0.02em]">A legacy of <span className="brand-gradient-text">excellence</span>.</h3>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {HONORS.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-start gap-4 py-3 border-b border-white/8"
                data-testid={`honor-${i}`}
              >
                <Award className="h-5 w-5 text-gold shrink-0 mt-0.5 stroke-[1.5]" />
                <span className="text-white/85 leading-relaxed">{h}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
}
